import { extrairErrosValidacao } from '../../escalas/core/escalaHelpers'
import { HorarioDisponivel, ParamsDisponibilidade } from './_models'

/* -------------------------------------------------------------------------- */
/* Query da disponibilidade                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Monta a query de `GET /agendamentos/disponibilidade`.
 *
 * ⚠️ O parâmetro do fim do intervalo é **`fim`**. O `/escalas/agenda` usa
 * `termino` — é outro endpoint, e copiar o nome de lá devolve 400.
 *
 * Opcional nulo é **omitido**, nunca enviado vazio: `especialidade=` em branco
 * chega no Spring como string vazia e quebra a conversão para `Long`.
 */
export const montaQueryDisponibilidade = (params: ParamsDisponibilidade): string => {
  const query = new URLSearchParams({
    empresa: String(params.empresa),
    tipo: params.tipo,
    inicio: params.inicio,
    fim: params.fim,
  })

  if (params.especialidade != null) query.set('especialidade', String(params.especialidade))
  if (params.unidade != null) query.set('unidade', String(params.unidade))
  if (params.profissional != null) query.set('profissional', String(params.profissional))

  return query.toString()
}

/* -------------------------------------------------------------------------- */
/* Datas e horas, sem `new Date(string)`                                      */
/* -------------------------------------------------------------------------- */

/**
 * `AAAA-MM-DD` do dia de hoje, no relógio local.
 *
 * Não usa `toISOString()` de propósito: ele converte para UTC e, num browser em
 * `America/Sao_Paulo`, à noite devolve o dia seguinte.
 */
export const hojeIso = (): string => formataDataIso(new Date())

/** `Date` → `AAAA-MM-DD`, sem passar por UTC. */
export const formataDataIso = (data: Date): string => {
  const mes = String(data.getMonth() + 1).padStart(2, '0')
  const dia = String(data.getDate()).padStart(2, '0')
  return `${data.getFullYear()}-${mes}-${dia}`
}

/** Soma dias a uma data `AAAA-MM-DD`, devolvendo outra `AAAA-MM-DD`. */
export const somaDias = (dataIso: string, dias: number): string => {
  const [ano, mes, dia] = dataIso.split('-').map(Number)
  return formataDataIso(new Date(ano, mes - 1, dia + dias))
}

/**
 * `yyyy-MM-dd HH:mm:ss` → `HH:mm`. Recorte de string, não `Date`: o valor vem
 * sem fuso e qualquer conversão o desloca.
 */
export const horaDoInicio = (inicio: string): string => inicio.slice(11, 16)

/** `yyyy-MM-dd HH:mm:ss` → `AAAA-MM-DD`. */
export const dataDoInicio = (inicio: string): string => inicio.slice(0, 10)

const DIAS_SEMANA = [
  'Domingo',
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
]

/** `2026-10-01` → `Quinta-feira, 01/10/2026`. */
export const formataDiaPorExtenso = (dataIso: string): string => {
  const [ano, mes, dia] = dataIso.split('-').map(Number)
  if (!ano || !mes || !dia) return dataIso
  // Construtor numérico: meia-noite LOCAL, sem o deslocamento de `new Date('2026-10-01')`.
  const nomeDoDia = DIAS_SEMANA[new Date(ano, mes - 1, dia).getDay()]
  return `${nomeDoDia}, ${String(dia).padStart(2, '0')}/${String(mes).padStart(2, '0')}/${ano}`
}

/* -------------------------------------------------------------------------- */
/* Estado de um horário                                                       */
/* -------------------------------------------------------------------------- */

export type EstadoHorario = 'livre' | 'esgotado' | 'excedente'

/**
 * Rótulo textual de cada estado. **A cor nunca é o único portador do
 * significado** (BRAND.md + WCAG 2.2 AA): o horário mostra sempre este texto e
 * o ícone, além do tom de fundo.
 */
export const ESTADO_HORARIO_LABELS: Record<EstadoHorario, string> = {
  livre: 'Livre',
  esgotado: 'Esgotado',
  excedente: 'Acima da capacidade',
}

/** Ícone (Bootstrap Icons) de cada estado — par obrigatório da cor. */
export const ESTADO_HORARIO_ICONES: Record<EstadoHorario, string> = {
  livre: 'bi-check-circle',
  esgotado: 'bi-slash-circle',
  excedente: 'bi-exclamation-triangle',
}

/**
 * Estado do horário. A fonte de "dá para reservar" é **`disponivel`**, decidido
 * pelo servidor — nunca `capacidade - ocupadas`: com `agenda_bloqueia_excedente`
 * desligado (padrão em todas as empresas) `ocupadas > capacidade` é estado legal
 * e o servidor ainda assim aceita a reserva.
 */
export const estadoDoHorario = (horario: HorarioDisponivel): EstadoHorario => {
  if (horario.capacidade != null && horario.ocupadas > horario.capacidade) return 'excedente'
  return horario.disponivel ? 'livre' : 'esgotado'
}

const plural = (quantidade: number, singular: string, pluralizado: string): string =>
  `${quantidade} ${quantidade === 1 ? singular : pluralizado}`

/**
 * Frase de ocupação da vaga, nos quatro casos que a API produz.
 *
 * | Caso | Texto |
 * | --- | --- |
 * | `capacidade == null` | `Sem limite de vagas · N reserva(s)` |
 * | `capacidade === 1` | `Livre` / `Ocupado` |
 * | `capacidade > 1` | `N de M vagas usadas · resta(m) K` |
 * | `ocupadas > capacidade` | `N de M vagas usadas (acima da capacidade)` |
 *
 * No modo pool a capacidade é hoje **sempre nula** (`capacidade_simultanea`
 * nasceu sem default e sem API), então a grade de equipe nunca mostra esgotado —
 * é o estado do dado, não defeito da tela. Esgotado só aparece escolhendo um
 * profissional, onde a capacidade da vaga nominal é 1.
 */
export const descreveOcupacao = (horario: HorarioDisponivel): string => {
  const { capacidade, ocupadas, disponivel } = horario

  if (capacidade == null) {
    return `Sem limite de vagas · ${plural(ocupadas, 'reserva', 'reservas')}`
  }
  if (ocupadas > capacidade) {
    return `${ocupadas} de ${capacidade} vagas usadas (acima da capacidade)`
  }
  if (capacidade === 1) {
    return disponivel ? 'Livre' : 'Ocupado'
  }
  const restantes = capacidade - ocupadas
  return `${ocupadas} de ${capacidade} vagas usadas · ${plural(
    restantes,
    'vaga restante',
    'vagas restantes'
  )}`
}

/** Como a vaga é nomeada em toda a tela: pool e nominal são fechaduras distintas. */
export const ROTULO_VAGA_POOL = 'Equipe (sem profissional definido)'

export const rotuloProfissional = (horario: HorarioDisponivel): string =>
  horario.profissional?.nome ?? ROTULO_VAGA_POOL

/**
 * Nome acessível do botão do horário — é o que o leitor de tela anuncia, então
 * carrega tudo o que a cor comunica visualmente.
 */
export const descreveHorarioParaLeitorDeTela = (horario: HorarioDisponivel): string => {
  const estado = estadoDoHorario(horario)
  const partes = [
    `${horario.disponivel ? 'Escolher' : 'Horário'} ${horaDoInicio(horario.inicio)}`,
    horario.duracaoMinutos ? `${horario.duracaoMinutos} minutos` : null,
    rotuloProfissional(horario),
    descreveOcupacao(horario),
    ESTADO_HORARIO_LABELS[estado],
  ]
  return partes.filter(Boolean).join(', ')
}

/* -------------------------------------------------------------------------- */
/* Conflito de reserva (409)                                                  */
/* -------------------------------------------------------------------------- */

/**
 * `true` quando o backend recusou por conflito de vaga
 * (`AgendaConflitoException` → 409).
 *
 * Concorrência por horário é **operação normal**, não defeito: o backend nem
 * manda para o Sentry. A tela trata como mensagem de negócio.
 */
export const ehConflitoDeAgenda = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 409

/**
 * Único texto que a tela acrescenta ao 409. A mensagem do servidor
 * ("Profissional indisponível neste horário.", "Horário sem vaga disponível.",
 * "Este paciente já possui reserva neste horário.") vai **literal**, sem
 * enriquecer: o índice `uk_agendamentos_pessoa_horario` atravessa tenants de
 * propósito, então dizer quem ocupou vazaria ocupação de outra empresa.
 */
export const MENSAGEM_PROXIMO_PASSO_CONFLITO = 'A grade foi atualizada; escolha outro horário.'

/** Recusa genérica, para quando o 409 chegar sem texto no envelope. */
export const MENSAGEM_CONFLITO_PADRAO = 'Este horário não está mais disponível.'

/* -------------------------------------------------------------------------- */
/* Falhas fora do 409                                                         */
/* -------------------------------------------------------------------------- */

/** Recusa por perfil: o 403 do Spring Security chega sem o envelope da API. */
export const MENSAGEM_SEM_PERMISSAO_AGENDA =
  'Seu perfil não tem permissão para agendar para este paciente. Fale com o administrador da instituição.'

/** Rede, timeout, CORS, 5xx: qualquer falha sem texto do servidor. */
export const MENSAGEM_FALHA_GENERICA_AGENDA =
  'Não foi possível concluir a operação. Verifique sua conexão e tente novamente.'

/**
 * Itens do aviso de erro para qualquer falha que não seja o 409.
 *
 * **Nunca devolve lista vazia**: sem envelope (`{data: {message, errors}}`) o
 * `extrairErrosValidacao` não acha texto nenhum, e o aviso sumiria — a grade
 * então mostraria "Nenhum horário no período" para uma falha do servidor, e o
 * operador diria ao paciente que não há vaga.
 */
export const descreveFalhaAgenda = (error: unknown): string[] => {
  const erros = extrairErrosValidacao(error)
  const porCampo = Object.entries(erros.porCampo).flatMap(([campo, mensagens]) =>
    mensagens.map((mensagem) => `${campo}: ${mensagem}`)
  )
  if (porCampo.length) return porCampo
  if (erros.mensagem) return [erros.mensagem]

  const status = (error as { response?: { status?: number } })?.response?.status
  return [status === 403 ? MENSAGEM_SEM_PERMISSAO_AGENDA : MENSAGEM_FALHA_GENERICA_AGENDA]
}
